import { useCallback, useEffect, useState } from 'react';
import { ImageSquare, Trash, UploadSimple } from '@phosphor-icons/react';
import { toast } from 'sonner';
import DesignDnaV2FontLibrary from './DesignDnaV2FontLibrary';

export default function DesignV3ProjectAssets({ api, projectId, onLogoChange }) {
  return (
    <aside className="rounded-2xl border border-[var(--border-color)] bg-black/10 p-4 space-y-5" data-testid="design-v3-project-assets">
      <div>
        <p className="text-[10px] uppercase tracking-[.18em] text-[var(--accent-purple)] font-semibold">Asset progetto</p>
        <h3 className="text-sm font-semibold mt-1">Font e logo</h3>
      </div>
      <DesignDnaV2FontLibrary api={api} projectId={projectId} compact />
      <div className="border-t border-[var(--border-color)] pt-4">
        <ProjectLogoUpload api={api} projectId={projectId} onChange={onLogoChange} />
      </div>
    </aside>
  );
}

export function ProjectLogoUpload({ api, projectId, onChange }) {
  const [logo, setLogo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [removing, setRemoving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/projects/${projectId}/design/v2/capabilities`);
      const next = data?.brand_logo;
      setLogo(next?.stored_filename || next?.filename ? next : null);
    } catch (error) {
      toast.error(`Logo progetto: ${error.response?.data?.detail || error.message}`);
    } finally {
      setLoading(false);
    }
  }, [api, projectId]);

  useEffect(() => { load(); }, [load]);

  const upload = async file => {
    if (!file || uploading) return;
    const form = new FormData();
    form.append('file', file);
    form.append('position', 'bottom-right');
    form.append('size_percent', '12');
    form.append('opacity', '1');
    setUploading(true);
    try {
      await api.post(`/projects/${projectId}/design/logo`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
      await load();
      onChange?.();
      toast.success('Logo del progetto salvato');
    } catch (error) {
      toast.error(`Logo progetto: ${error.response?.data?.detail || error.message}`);
    } finally {
      setUploading(false);
    }
  };

  const remove = async () => {
    if (!logo || removing) return;
    setRemoving(true);
    try {
      await api.delete(`/projects/${projectId}/design/logo`);
      setLogo(null);
      onChange?.();
      toast.success('Logo del progetto rimosso');
    } catch (error) {
      toast.error(`Logo progetto: ${error.response?.data?.detail || error.message}`);
    } finally {
      setRemoving(false);
    }
  };

  return <div className="space-y-3" data-testid="design-v3-project-logo">
    <div>
      <p className="text-[10px] uppercase tracking-[.18em] text-[var(--accent-purple)] font-semibold">Logo</p>
      <h4 className="text-sm font-semibold mt-1">Logo del progetto</h4>
      <p className="text-[11px] leading-relaxed text-[var(--text-muted)] mt-1">Caricalo una volta. Nell’editor, + Logo inserisce questo file sul canvas.</p>
    </div>
    {loading ? <p className="text-xs text-[var(--text-muted)]">Carico logo…</p> : <div className="rounded-xl border border-[var(--border-color)] p-3 space-y-3">
      <div className="h-24 rounded-lg bg-black/25 flex items-center justify-center overflow-hidden">
        {logo?.url ? <img src={logo.url} alt="Logo progetto" className="max-w-full max-h-full object-contain"/> : <ImageSquare size={24} className="text-[var(--text-muted)]"/>}
      </div>
      <p className="text-[10px] text-[var(--text-muted)] truncate" title={logo?.original_name || ''}>{logo?.original_name || 'Nessun logo caricato'}</p>
      <div className="flex gap-2">
        <label className={`btn-gradient text-[11px] flex-1 justify-center cursor-pointer ${uploading ? 'opacity-60 pointer-events-none' : ''}`} data-testid="design-v3-project-logo-upload">
          <UploadSimple size={13}/>{uploading ? 'Caricamento…' : logo ? 'Sostituisci' : 'Carica logo'}
          <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={event=>{const file=event.target.files?.[0];event.target.value='';upload(file);}}/>
        </label>
        {logo&&<button type="button" className="btn-ghost !px-3 text-rose-400" title="Rimuovi logo" aria-label="Rimuovi logo" disabled={removing} onClick={remove}><Trash size={14}/></button>}
      </div>
    </div>}
  </div>;
}
